import React from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'
import type { ExperiencesPassportOfferSource } from 'components/Experiences/ExperiencesPassport/experiencesPassportTypes'
import BodyText from 'LuxKit/Typography/BodyText'
import { tierDarkForegroundColorStyle } from 'styles/utils/tierStyles'

import PropertyRateInclusionsStack, { type PropertyRateInclusionListsProps } from './PropertyRateInclusionsStack'

const UpgradedInclusionsHeading = styled(BodyText)`
  ${tierDarkForegroundColorStyle}
`

/**
 * Cross-fade + slide between the base and target rate inclusions when the package upgrade toggles
 */
const InclusionsSwapContainer = styled.div`
  display: grid;
  grid-template-areas: 'base-rate' 'target-upgrade-rate';
  grid-template-rows: 1fr;
`

const BaseRateInclusions = styled.div`
  grid-area: base-rate;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

const TargetUpgradeRateInclusions = styled.div`
  grid-area: target-upgrade-rate;
  grid-row: 1;
  opacity: 1;
  transform: translateY(0);
  transition: opacity 300ms ease, transform 300ms ease;
  transition-delay: 300ms;

  &:not(.show) {
    opacity: 0;
    transform: translateY(${rem(-16)});
    transition-delay: 0ms;
    pointer-events: none;
  }
`

interface Props extends PropertyRateInclusionListsProps {
  passportOffer?: ExperiencesPassportOfferSource
  upgradeTargetRate?: App.EnrichedPropertyRate
  upgradeApplied?: boolean
  showReturnFlightsInclusion: boolean
  rooms: Array<App.Occupants>
  displayTier: App.LoyaltyTier
  contentRef: React.Ref<HTMLDivElement>
  inclusionsSwapContainerRef: React.Ref<HTMLDivElement>
  baseRateInclusionsRef: React.Ref<HTMLDivElement>
  targetUpgradeRateInclusionsRef: React.Ref<HTMLDivElement>
}

function PropertyExpandedRateInclusions({
  property,
  inclusions,
  bonusInclusions,
  luxPlusInclusions,
  bundleInclusions,
  passportOffer,
  upgradeTargetRate,
  upgradeApplied,
  showReturnFlightsInclusion,
  rooms,
  displayTier,
  contentRef,
  inclusionsSwapContainerRef,
  baseRateInclusionsRef,
  targetUpgradeRateInclusionsRef,
}: Props) {
  const intl = useIntl()

  const baseStack = <PropertyRateInclusionsStack
    property={property}
    inclusions={inclusions}
    bonusInclusions={bonusInclusions}
    luxPlusInclusions={luxPlusInclusions}
    bundleInclusions={bundleInclusions}
    showReturnFlightsInclusion={showReturnFlightsInclusion}
    rooms={rooms}
  />

  return (
    <VerticalSpacer ref={contentRef} gap={12}>
      {upgradeTargetRate && <InclusionsSwapContainer ref={inclusionsSwapContainerRef}>
        <BaseRateInclusions className={upgradeApplied ? undefined : 'show'}>
          <div ref={baseRateInclusionsRef}>
            {baseStack}
          </div>
        </BaseRateInclusions>
        <TargetUpgradeRateInclusions className={upgradeApplied ? 'show' : undefined}>
          <div ref={targetUpgradeRateInclusionsRef}>
            <VerticalSpacer gap={4}>
              <UpgradedInclusionsHeading variant="medium" weight="bold" className={`tier-${displayTier}`}>
                {intl.formatMessage({
                  id: 'HRYT9P5ypghRMebUdoEQ',
                  defaultMessage: 'Your upgraded inclusions',
                  description: 'Heading above the inclusions of the package a loyalty member has upgraded to on the offer page rate tile',
                })}
              </UpgradedInclusionsHeading>
              <PropertyRateInclusionsStack
                property={property}
                inclusions={upgradeTargetRate.inclusions}
                bonusInclusions={upgradeTargetRate.bonusInclusions}
                luxPlusInclusions={upgradeTargetRate.luxPlusInclusions}
                bundleInclusions={upgradeTargetRate.bundleInclusions}
                showReturnFlightsInclusion={showReturnFlightsInclusion}
                rooms={rooms}
              />
            </VerticalSpacer>
          </div>
        </TargetUpgradeRateInclusions>
      </InclusionsSwapContainer>}
      {!upgradeTargetRate && baseStack}
      {passportOffer && <ExperiencesPassportOfferSummary offer={passportOffer} />}
    </VerticalSpacer>
  )
}

export default PropertyExpandedRateInclusions
